/**
 * Self-Improving Workflow Demo — oRPC procedure resolver.
 *
 * THROWAWAY DEMO CODE. Maps workflow oRPC procedure paths to fixtures so the
 * REAL /workflows list + detail + runs render the seeded dataset with zero
 * network. Returns `undefined` for anything it doesn't own (so auth, nav, org
 * config, … fall through to the real backend). Because it sits at the oRPC link
 * layer, it also satisfies the workflow-detail route loader's `ensureQueryData`
 * calls (getWorkflow / getDefinition) — no per-hook shims needed.
 *
 * Delete alongside frontend/src/demo/workflow-recovery.
 */

import type { DemoProcedureResolver } from '@/demo/core/demo-client';
import { getWorkflowRecoveryState } from './fixture-store';

function asRecord(input: unknown): Record<string, unknown> {
  return (input ?? {}) as Record<string, unknown>;
}

export const workflowRecoveryResolver: DemoProcedureResolver = (
  path,
  input,
) => {
  const key = path.join('.');
  const state = getWorkflowRecoveryState();
  const inp = asRecord(input);

  switch (key) {
    case 'workflows.listDefinitionSummaries':
      return {
        workflows: state.summaries,
        total: state.summaries.length,
        availableTags: [
          'accounts-receivable',
          'reminders',
          'accounts-payable',
          'oms',
        ],
      };
    case 'workflows.getWorkflow':
      return state.workflowsById[String(inp.workflowId)];
    case 'workflows.getDefinition':
      return state.definitionsById[String(inp.workflowDefinitionId)];
    case 'workflows.listRunsForDefinition':
      return state.runsByDefId[String(inp.workflowDefinitionId)];
    // Quiet the secondary calls the workflow pages make so nothing 404s on the
    // seeded (non-real) ids while the demo is active.
    case 'workflows.listTestCases':
      return { testCases: [] };
    case 'workflows.orchestratorHealth':
      return { rabbitmq: true, temporal: true };

    default:
      return undefined;
  }
};
